// Lightweight emoji picker for workflow icons.
// Renders a compact grid popover with curated emojis grouped by category.
// No external dependencies — just a flat array + CSS grid.
import { useEffect, useRef, type CSSProperties } from 'react';
import { APP } from '../tokens';

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
}

// Curated set — biased toward document/business/productivity use cases.
const EMOJI_GROUPS: { label: string; emojis: string[] }[] = [
  { label: 'Documents', emojis: ['📄', '📝', '📋', '📑', '📃', '📜', '🗒️', '🗂️', '📁', '📂', '📊', '📈', '📉'] },
  { label: 'Business',  emojis: ['💼', '🏢', '🤝', '💰', '💳', '🏦', '📨', '📩', '✉️', '📧', '🧾', '🪪'] },
  { label: 'Legal',     emojis: ['⚖️', '🔏', '🔐', '🛡️', '✅', '❌', '📰', '🏛️'] },
  { label: 'People',    emojis: ['👤', '👥', '🧑‍💼', '👷', '🏠', '🏗️'] },
  { label: 'Time',      emojis: ['⏰', '📅', '🕐', '⏳', '🔔', '📆'] },
  { label: 'Symbols',   emojis: ['⚡', '🚀', '⭐', '💡', '🎯', '🔥', '💎', '✨', '📌', '🏷️', '🔗', '🔍'] },
  { label: 'Status',    emojis: ['🟢', '🟡', '🔴', '🟠', '🔵', '🟣'] },
  { label: 'Misc',      emojis: ['🎨', '📷', '🎵', '📱', '💻', '🌐', '🧩', '📐', '🔧', '⚙️'] },
];

export function EmojiPicker({ onSelect, onClose }: EmojiPickerProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div ref={ref} style={CONTAINER}>
      {EMOJI_GROUPS.map((group) => (
        <div key={group.label}>
          <div style={GROUP_LABEL}>{group.label}</div>
          <div style={GRID}>
            {group.emojis.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => onSelect(emoji)}
                style={EMOJI_BTN}
                onMouseEnter={(e) => { (e.currentTarget.style.backgroundColor) = `${APP.textMuted}18`; }}
                onMouseLeave={(e) => { (e.currentTarget.style.backgroundColor) = 'transparent'; }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────

const CONTAINER: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  width: 280,
  maxHeight: 320,
  overflowY: 'auto',
  backgroundColor: APP.bgMain,
  border: `1px solid ${APP.border}`,
  borderRadius: 10,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  padding: '8px 6px',
  zIndex: 300,
  fontFamily: APP.fontFamily,
};

const GROUP_LABEL: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: APP.textMuted,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  padding: '6px 4px 2px',
};

const GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 2,
};

const EMOJI_BTN: CSSProperties = {
  width: 34,
  height: 34,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
  border: 'none',
  background: 'transparent',
  borderRadius: 6,
  cursor: 'pointer',
  padding: 0,
  lineHeight: 1,
};
